import { useTranslation } from 'react-i18next'

import {
  Flex,
  PrimaryButton,
  SecondaryButton,
  TertiaryButton,
  chakra,
} from '@enechain/ecloud-designsystem'

import { Modal } from '~/components/common/Modal'

type Props = {
  isOpen: boolean
  /** 「保存して移動」処理中はボタンを loading にする。 */
  saving: boolean
  /**
   * 「保存して移動」を非活性にする。過去日ビュー (conventions.md §GC ロック:
   * 全コマ GC 済み) では保存経路自体を封鎖するため、破棄経路のみを残す。
   * 既定 false。
   */
  saveDisabled?: boolean
  /** 保存不可のときに、次の操作を画面上の文で案内する。 */
  saveDisabledReason?: string
  onSaveAndLeave: () => void
  onDiscardAndLeave: () => void
  onCancel: () => void
}

/**
 * 未保存変更がある状態でアプリ内遷移・画面内の編集対象切替をしようとした時の
 * 3 択ダイアログ (AC-05/AC-07)。「保存して移動 / 破棄して移動 / キャンセル」。
 * per-小売保存モデルのため「保存して移動」は dirty な全小売を順に保存する
 * (= 親 index.tsx が実装、useForecastDraftEditor.executeSaveAll)。
 *
 * AdminApprovalFlow の UnsavedLeaveDialog と同型 (page-local に複製。理由は
 * useUnsavedGuard の JSDoc 参照)。
 */
export const UnsavedLeaveDialog = ({
  isOpen,
  saving,
  saveDisabled = false,
  saveDisabledReason,
  onSaveAndLeave,
  onDiscardAndLeave,
  onCancel,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <Modal
      footer={
        <Flex alignItems="center" gap="8px" width="100%">
          <SecondaryButton
            marginRight="auto"
            disabled={saving}
            onClick={onDiscardAndLeave}
            size="sm"
          >
            {t('page:demandForecastDaily.leaveGuard.discardAndLeave')}
          </SecondaryButton>
          <TertiaryButton disabled={saving} onClick={onCancel} size="sm">
            {t('page:demandForecastDaily.leaveGuard.cancel')}
          </TertiaryButton>
          <PrimaryButton
            disabled={saveDisabled}
            loading={saving}
            onClick={onSaveAndLeave}
            size="sm"
          >
            {t('page:demandForecastDaily.leaveGuard.saveAndLeave')}
          </PrimaryButton>
        </Flex>
      }
      isOpen={isOpen}
      onClose={onCancel}
      // 幅は UI 辞書 components.md `.ecl-modal` の 4 段 (既定 md)。 8080 に対応する画面が無いので既定の md
      size="md"
      title={t('page:demandForecastDaily.leaveGuard.title')}
    >
      <chakra.p margin={0}>
        {t('page:demandForecastDaily.leaveGuard.body')}
      </chakra.p>
      {saveDisabledReason !== undefined && (
        <chakra.p marginTop={2} marginBottom={0}>
          {saveDisabledReason}
        </chakra.p>
      )}
    </Modal>
  )
}
